















































































import Component from "vue-class-component";

import { RingLoader } from "vue-spinners-css";

import _auth from "@/utils/auth";
import { BaseRootPageComponent } from "../BaseRootPageComponent";
import _gateway from "@/utils/gateway";
import OneTimePassword from "./OneTimePassword.vue";
import { PhoneNumberUtil } from "google-libphonenumber";
import { VerificationMethod } from "@/models";
import { updateGTMGlobalVariables } from "@/utils/gtm";
import { EventBus, EventNames } from "@/utils/eventBus";
import { logError, SeverityLevel } from "@/utils/appInsights";

@Component({
  components: { RingLoader, OneTimePassword }
})
export default class TwoFactorAuthentication extends BaseRootPageComponent {
  // Use a class property for dependencies so unit tests can override them
  auth = _auth;
  gateway = _gateway;

  isDestroyed = false;

  verificationMethod = VerificationMethod;
  method = VerificationMethod.Email;
  error = "";
  loading = false;
  showOtp = false;
  showTimer = true;

  created() {
    this.setTitle("Verification Code");
    // updateGTMGlobalVariables(this);

    // If already logged in, redirect to the home page
    if (this.auth.isLoggedIn()) this.$router.push("/");

    if (this.auth.user?.phone) {
      this.method = VerificationMethod.Mobile;
    } else {
      this.method = VerificationMethod.Email;
    }

    this.subscribeToEvents();
  }

  subscribeToEvents() {
    // If the component has already been destroyed (e.g. the user navigated away while the initial data was being
    // loaded), don't register for the data refresh event otherwise, it will never be removed and will keep running
    // until the user reloads the webpage.
    if (this.isDestroyed) return;

    EventBus.$on(EventNames.DataRefresh, this.checkForExpiredToken);
  }

  beforeDestroy() {
    EventBus.$off(EventNames.DataRefresh, this.checkForExpiredToken);
    this.isDestroyed = true;
  }

  get maskedEmail() {
    if (!this.auth.user?.email) return "";

    const characters: string[] = this.auth.user.email.split("");
    let len = characters.indexOf("@");
    // If, for some reason, the email doesn't have an @, mask the entire thing
    if (len < 0) len = characters.length;
    return characters.map((c, index) => (index >= 1 && index <= len - 2 ? "*" : c)).join("");
  }

  get maskedPhoneNumber() {
    if (!this.auth.user?.phone) return "";

    try {
      // If the phone number is not formatted correctly, the library will throw an error
      const phoneUtil = PhoneNumberUtil.getInstance();
      const number = phoneUtil.parseAndKeepRawInput(this.auth.user.phone);
      const nationalNum = number.getNationalNumber();
      return nationalNum?.toString().replace(/\d(?=\d{4})/g, "*") || "";
    } catch (error) {
      logError(this, error, SeverityLevel.Warning, {
        message: "Cannot parse user's phone number. Only allowing email for 2FA",
        phoneNumber: this.auth.user.phone
      });

      // If there's an error with the phone, just treat it like no phone number exists
      return "";
    }
  }

  get selectedAuthValue() {
    return this.method === VerificationMethod.Email ? this.maskedEmail : this.maskedPhoneNumber;
  }

  /**
   * On the data refresh interval, check to see if the user's token has expired. If it has, redirect them back to the
   * sign-in page.
   */
  checkForExpiredToken() {
    // If the component has already been destroyed, no need to check the user's session
    if (this.isDestroyed) return;

    // Once the user enters a valid 2FA, they will be fully logged in. However, they may not be redirected yet because
    // of pending API calls (e.g. adding a trusted device) or user interaction (e.g. setting up biometrics). Do not
    // redirect the user in this case, rely on the process that logged them in to redirect them once all additional
    // processing has finished.
    if (this.auth.isLoggedIn()) return;

    if (!this.auth.isLoggedInTo2fa()) {
      this.$router.push({ path: "/sign-in", query: { redirectTo: this.$route.query.redirectTo } });
    }
  }

  selectMethod(type: VerificationMethod) {
    this.method = type;
    this.error = "";
  }

  async redirectToSignIn() {
    await this.auth.logout();
    this.$router.push("/sign-in");
  }

  async requestVerificationCode() {
    this.loading = true;
    this.error = "";
    try {
      await this.gateway.requestVerificationCode(this.method);
      this.showOtp = true;
    } catch (error) {
      this.error = "An unknown error occurred";
    } finally {
      this.loading = false;
    }
  }
}
